export const PARTNER_WHATSAPP_EXPORT_ENABLED = true;

export const PARTNER_WHATSAPP_EXPORT_ROLES = Object.freeze(["owner", "manager"]);

export const PARTNER_WHATSAPP_RANGE_OPTIONS = Object.freeze([
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
]);

export const PARTNER_WHATSAPP_STATUS_LABELS = Object.freeze({
  PENDING_BUYER_WHATSAPP_CONFIRMATION: "Waiting for buyer confirmation",
  OFFERS_SENT: "Dealer contacted",
  VENDOR_ACCEPTED: "Dealer interested",
  PENDING_LEAD_CONSENT: "Waiting for buyer share consent",
  HANDOFF_COMPLETED: "Connected",
  CONNECTED: "Connected",
  PENDING_MANUAL_DEALER_OUTREACH: "Manual outreach needed",
  FAILED: "Failed",
  DECLINED: "Declined",
  REJECTED: "Declined",
  EXPIRED: "Expired",
});

export const PARTNER_WHATSAPP_ATTENTION_META = Object.freeze({
  stuck_buyer_confirmation: {
    label: "Stuck buyer confirmations",
    comingSoonRoute: "whatsapp/conversations",
  },
  manual_outreach_unresolved: {
    label: "Manual outreach unresolved",
    comingSoonRoute: "whatsapp/conversations",
  },
  template_send_failed: {
    label: "Failed template sends",
    comingSoonRoute: "whatsapp/conversations",
  },
  dealers_failed_alerts: {
    label: "Dealers with repeated failed alerts",
    dealerHealthStatus: "needs_attention",
  },
  dealers_missing_invalid_whatsapp: {
    label: "Dealers missing or invalid WhatsApp",
    dealerHealthStatus: "missing_whatsapp",
  },
});

export const PARTNER_WHATSAPP_NEXT_ACTIONS = Object.freeze([
  {
    id: "review_stuck",
    label: "Review stuck leads",
    filter: { stuck: true },
  },
  {
    id: "dealer_health",
    label: "Check dealer WhatsApp health",
    hrefSegment: "whatsapp/dealers",
  },
  {
    id: "manual_outreach",
    label: "Review manual outreach items",
    filter: { manualOutreachRequired: true },
  },
]);

export const PARTNER_WHATSAPP_OUTCOME_LABELS = Object.freeze({
  pending_confirmation: "Waiting for buyer confirmation",
  contacting_dealer: "Dealer contacted",
  dealer_interested: "Dealer interested",
  buyer_consent_pending: "Waiting for buyer consent",
  connected: "Connected",
  dealer_passed: "Dealer passed",
  manual_outreach: "Manual outreach needed",
  failed: "Failed",
  expired: "Expired",
  unknown: "In progress",
});

export const PARTNER_WHATSAPP_OUTCOME_FILTERS = Object.freeze([
  { value: "", label: "All outcomes" },
  { value: "pending_confirmation", label: "Waiting for buyer confirmation" },
  { value: "contacting_dealer", label: "Dealer contacted" },
  { value: "dealer_interested", label: "Dealer interested" },
  { value: "buyer_consent_pending", label: "Waiting for buyer consent" },
  { value: "connected", label: "Connected" },
  { value: "manual_outreach", label: "Manual outreach needed" },
  { value: "failed", label: "Failed" },
]);

export const PARTNER_WHATSAPP_STATUS_FILTERS = Object.freeze([
  { value: "", label: "All broker statuses" },
  { value: "PENDING_BUYER_WHATSAPP_CONFIRMATION", label: "Waiting for buyer confirmation" },
  { value: "OFFERS_SENT", label: "Dealer contacted" },
  { value: "VENDOR_ACCEPTED", label: "Dealer interested" },
  { value: "PENDING_LEAD_CONSENT", label: "Waiting for buyer share consent" },
  { value: "HANDOFF_COMPLETED", label: "Connected" },
  { value: "PENDING_MANUAL_DEALER_OUTREACH", label: "Manual outreach needed" },
  { value: "FAILED", label: "Failed" },
]);

export function partnerWhatsappOutcomeLabel(outcome) {
  if (!outcome) return "In progress";
  return PARTNER_WHATSAPP_OUTCOME_LABELS[outcome] || "In progress";
}

export function partnerWhatsappStatusLabel(status) {
  if (!status) return "Unknown";
  return PARTNER_WHATSAPP_STATUS_LABELS[status] || "In progress";
}

export const PARTNER_WHATSAPP_DEALER_HEALTH_LABELS = Object.freeze({
  ready: "Ready",
  needs_attention: "Needs attention",
  missing_whatsapp: "Missing WhatsApp",
  invalid_phone: "Invalid phone",
  not_synced: "Not synced",
  inactive: "Inactive",
  low_response: "Low response",
  manual_outreach_heavy: "Manual outreach heavy",
  no_activity: "No recent activity",
  unknown: "Unknown",
});

export const PARTNER_WHATSAPP_DEALER_HEALTH_STATUS_FILTERS = Object.freeze([
  { value: "all", label: "All dealers" },
  { value: "ready", label: "Ready" },
  { value: "needs_attention", label: "Needs attention" },
  { value: "missing_whatsapp", label: "Missing WhatsApp" },
  { value: "invalid_phone", label: "Invalid phone" },
  { value: "not_synced", label: "Not synced" },
  { value: "low_response", label: "Low response" },
  { value: "manual_outreach", label: "Manual outreach heavy" },
]);

export const PARTNER_WHATSAPP_DEALER_HEALTH_SORT_OPTIONS = Object.freeze([
  { value: "healthStatus", label: "Health status" },
  { value: "dealerName", label: "Dealer name" },
  { value: "acceptRate", label: "Accept rate" },
  { value: "manualOutreachCount", label: "Manual outreach" },
  { value: "failedAlertCount", label: "Failed alerts" },
  { value: "lastActivityAt", label: "Last activity" },
]);

export function buildPartnerWhatsappConversationHref(basePath, filters = {}) {
  const params = new URLSearchParams();
  if (filters.range === "7d" || filters.range === "30d") {
    params.set("range", filters.range);
  }
  if (filters.dealerId) params.set("dealerId", filters.dealerId);
  if (filters.leadBrokerStatus) params.set("leadBrokerStatus", filters.leadBrokerStatus);
  if (filters.outcome) params.set("outcome", filters.outcome);
  if (filters.stuck === true || filters.stuck === "true") params.set("stuck", "true");
  if (filters.manualOutreachRequired === true || filters.manualOutreachRequired === "true") {
    params.set("manualOutreachRequired", "true");
  }
  if (filters.search) params.set("search", filters.search);
  if (filters.page && filters.page > 1) params.set("page", String(filters.page));
  const qs = params.toString();
  return qs ? `${basePath}?${qs}` : basePath;
}

export function buildPartnerWhatsappDealerHealthHref(basePath, filters = {}) {
  const params = new URLSearchParams();
  if (filters.range === "7d" || filters.range === "30d") {
    params.set("range", filters.range);
  }
  if (filters.status && filters.status !== "all") {
    params.set("status", filters.status);
  }
  if (filters.search) params.set("search", filters.search);
  if (filters.sort) params.set("sort", filters.sort);
  if (filters.direction) params.set("direction", filters.direction);
  if (filters.page && filters.page > 1) params.set("page", String(filters.page));
  const qs = params.toString();
  return qs ? `${basePath}?${qs}` : basePath;
}
